/**
 * Las 11 métricas de unit economics con **fórmula y benchmark a la vista**.
 *
 * Dos reglas de la spec se ven acá:
 *
 *  - Una métrica sin dato no muestra "—" ni cero: muestra `EmptyMetricState` con
 *    el motivo concreto ("No se carga a mano. Sale de LTV / CAC. Falta LTV y
 *    CAC.") y la acción que corresponde, que para una derivada es cargar su
 *    insumo y no el resultado.
 *  - La fórmula viaja en la fila, no en un tooltip: un número que no se puede
 *    recalcular a mano no se puede auditar.
 *
 * Los filtros son de la pantalla y no de la tabla: `DataTable` filtra por texto,
 * pero "sin dato" y "vencidas" son estados, no palabras.
 */
import { formatMetricValue } from '@ops/contracts'
import { useState } from 'react'

import {
  Button,
  DataTable,
  EmptyMetricState,
  HealthDot,
  Panel,
  ProvenanceBadge,
  Stack,
  StatusPill,
  type Column,
} from '../../ui'
import type { FilaMetrica } from './dependencias'

const FILTROS = [
  { id: 'todas', label: 'Todas' },
  { id: 'sin-dato', label: 'Sin dato' },
  { id: 'con-dato', label: 'Con dato' },
  { id: 'vencidas', label: 'Vencidas' },
] as const

type FiltroId = (typeof FILTROS)[number]['id']

function pasa(fila: FilaMetrica, filtro: FiltroId): boolean {
  if (filtro === 'sin-dato') return !fila.conDato
  if (filtro === 'con-dato') return fila.conDato
  if (filtro === 'vencidas') return fila.vencida
  return true
}

export interface TablaUnitEconomicsProps {
  filas: readonly FilaMetrica[]
  now: Date
  onCargar: (slug: string) => void
}

export function TablaUnitEconomics({
  filas,
  now,
  onCargar,
}: TablaUnitEconomicsProps) {
  const [filtro, setFiltro] = useState<FiltroId>('todas')
  const visibles = filas.filter((fila) => pasa(fila, filtro))

  const conteo = (id: FiltroId): number =>
    filas.filter((fila) => pasa(fila, id)).length

  const columnas: Column<FilaMetrica>[] = [
    {
      key: 'nombre',
      header: 'Métrica',
      rowHeader: true,
      width: '170px',
      sortValue: (fila) => fila.nombre,
      render: (fila) => (
        <span className="inline-flex flex-col gap-0.5">
          <span>{fila.nombre}</span>
          {fila.derivadaDe.length > 0 ? (
            <span className="text-micro font-normal text-muted-strong">
              la calcula el panel
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'valor',
      header: 'Valor',
      align: 'right',
      width: '190px',
      sortValue: (fila) => fila.metric.valor,
      render: (fila) => {
        if (fila.conDato) {
          return (
            <span className="tabular text-titulo font-semibold text-text">
              {formatMetricValue(fila.metric.valor, fila.metric.unidad)}
            </span>
          )
        }

        // La acción de una derivada es cargar su insumo: "Cargar CAC" en la fila
        // de Payback dice qué hay que hacer, "Cargalo acá" mentiría.
        const accion = fila.slugAccion
        return (
          <EmptyMetricState
            metricNombre={fila.nombre}
            detalle={fila.motivo ?? undefined}
            size="sm"
            className="items-end text-right"
            {...(accion === null
              ? {}
              : {
                  onLoadData: () => onCargar(accion),
                  textoAccion:
                    accion === fila.slug
                      ? 'Cargalo acá'
                      : `Cargar ${nombreDe(filas, accion)}`,
                })}
          />
        )
      },
    },
    {
      key: 'formula',
      header: 'Fórmula y benchmark',
      width: '260px',
      sortValue: (fila) => fila.metric.formula,
      render: (fila) => (
        <span className="inline-flex flex-col gap-0.5">
          <code className="rounded border border-border bg-n100 px-1.5 py-0.5 font-mono text-micro text-text">
            {fila.metric.formula ?? 'sin fórmula declarada'}
          </code>
          <span className="text-micro text-muted-strong">
            {fila.metric.benchmark ?? 'sin benchmark definido'}
          </span>
        </span>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      width: '120px',
      sortValue: (fila) => fila.metric.health,
      render: (fila) => <HealthDot health={fila.metric.health} size="sm" />,
    },
    {
      key: 'provenance',
      header: 'Proveniencia',
      width: '220px',
      filterValue: (fila) => fila.metric.provenance.source,
      render: (fila) =>
        fila.conDato ? (
          <ProvenanceBadge
            provenance={fila.metric.provenance}
            now={now}
            hrefAdmin="/admin"
          />
        ) : (
          // Sin dato no hay fuente que declarar: el badge "sin declarar" diría
          // que falta configurar una fuente, y lo que falta es el número.
          <span className="text-micro text-muted-strong">
            nunca se cargó · {fila.cargable ? 'carga manual' : 'derivada'}
          </span>
        ),
    },
  ]

  return (
    <Panel
      titulo={`Las ${filas.length} métricas`}
      subtitulo="Ordenables por cualquier columna. El orden inicial pone la deuda primero."
      padding="none"
      acciones={
        <Stack
          direccion="horizontal"
          gap="xs"
          wrap
          align="center"
          className="rounded-md border border-border bg-surface p-0.5"
        >
          {FILTROS.map((opcion) => (
            <Button
              key={opcion.id}
              variante={filtro === opcion.id ? 'secundario' : 'sutil'}
              onClick={() => setFiltro(opcion.id)}
              aria-pressed={filtro === opcion.id}
              className="h-6"
            >
              {opcion.label}
              <span className="ml-1 tabular font-bold text-muted-strong">
                {conteo(opcion.id)}
              </span>
            </Button>
          ))}
        </Stack>
      }
      footer={
        <span>
          Las derivadas no se cargan a mano: su acción carga el insumo. Cargar un
          resultado que el panel calcula deja dos versiones del mismo número.
        </span>
      }
    >
      <DataTable
        columns={columnas}
        rows={visibles}
        rowKey={(fila) => fila.slug}
        rowSeverity={(fila) => fila.metric.health}
        densidadPorSeveridad
        emptyMessage={
          filtro === 'vencidas'
            ? 'Ninguna carga manual está vencida.'
            : filtro === 'sin-dato'
              ? 'No queda ninguna métrica sin dato.'
              : 'Sin métricas para mostrar.'
        }
        filtroPlaceholder="Buscar métrica"
        caption={
          <>
            Unit economics del período con su valor, fórmula, benchmark, semáforo,
            proveniencia y acción de carga.
          </>
        }
        footer={
          <span>
            {visibles.length === filas.length
              ? `${filas.length} métricas`
              : `${visibles.length} de ${filas.length} métricas`}
            {filtro === 'todas' ? '' : ` · filtro "${etiquetaDe(filtro)}"`}
          </span>
        }
      />
    </Panel>
  )
}

function etiquetaDe(id: FiltroId): string {
  return FILTROS.find((opcion) => opcion.id === id)?.label ?? id
}

function nombreDe(filas: readonly FilaMetrica[], slug: string | null): string {
  if (slug === null) return 'el insumo'
  return filas.find((fila) => fila.slug === slug)?.nombre ?? slug
}
